import _uniq from "lodash/uniq";

import { Context } from "../context";
import { Frames } from "../util/frames";
import { Boxscore, Player } from "mannequin/src/types/statsapi";
import {
  GameEvent,
  Hand,
  InningState,
} from "mannequin/src/types/tracking/mannequin/game-event";

enum GameMode {
  LIVE = "live",
  WARMUP = "warmup",
}

export interface GameState {
  inning?: {
    currentInning: number;
    state: InningState;
    battingTeamId?: number;
    fieldingTeamId?: number;
  };
  atBat?: {
    state: "start" | "end";
    index: number;
  };
  play?: {
    state: "start" | "end";
    index: number;
    playId?: string;
    strikezone: {
      top?: number;
      bottom?: number;
    };
  };
  hands: {
    pitchHand?: Hand;
    batHand?: Hand;
  };
  count?: {
    balls?: number;
    strikes?: number;
    outs?: number;
  };

  liveAction?: boolean;

  gumboEventTimecode?: string;

  trackPositions: Map<number, number>;

  gameMode?: GameMode;
}

export class GameStateManager {
  private state: GameState = { trackPositions: new Map(), hands: {} };

  private contextRef?: Context;
  private boxscore?: Boxscore;

  private lastFrameNum: number | undefined = undefined;

  async init(context: Context) {
    this.contextRef = context;
    this.boxscore = context.bufferManager.metadata?.boxscore;
  }

  update(frames: Frames) {
    if (!this.contextRef) {
      console.error(`can't poll uninitalized game state`);
      return undefined;
    }

    let current = frames.current;
    if (!current || this.lastFrameNum == current.num) {
      return;
    }

    this.processFrames(frames);
    this.lastFrameNum = current.num;
  }

  private processFrames(frames: Frames) {
    if (!frames.current) return false;

    const events: GameEvent[] = [];
    const framesToProcess = [...frames.missed, frames.current];

    for (const frame of framesToProcess) {
      if (frame.gameEvents == undefined || frame.gameEvents.length == 0) {
        continue;
      }

      events.push(...frame.gameEvents);
    }

    for (const event of events) {
      switch (event.type) {
        case "count":
          this.state.count = event.data;
          break;

        case "isLiveAction":
          this.state.liveAction = event.data.mode;
          break;

        case "inning":
          this.state.inning = {
            currentInning: event.data.currentInning,
            state: event.data.action,
            battingTeamId: event.data.battingTeamId,
            fieldingTeamId: event.data.fieldingTeamId,
          };
          break;

        case "atBat":
          this.state.atBat = {
            state: event.data.action,
            index: event.data.index,
          };
          break;

        case "play":
          this.state.play = {
            state: event.data.action,
            index: event.data.index,
            playId: event.data.playId,
            strikezone: {
              top: event.data.strikezone?.top,
              bottom: event.data.strikezone?.bottom,
            },
          };
          break;

        case "handed":
          if (event.data.action == "pitch") {
            this.state.hands.pitchHand = event.data.side;
          }
          if (event.data.action == "bat") {
            this.state.hands.batHand = event.data.side;
          }
          break;

        case "positionAssignment":
          this.state.trackPositions.set(
            event.data.trackUid,
            event.data.newPosition
          );
          break;

        case "timecode":
          this.state.gumboEventTimecode = event.data.timecode;
          this.contextRef?.callbacks.onGumboChange(event.data.timecode);
          break;

        default:
          break;
      }
    }
  }

  // boxscore state
  getTeams() {
    return this.boxscore?.teams;
  }

  getPlayerByActorId(id: number): Player | undefined {
    const teams = this.getTeams();
    return id != undefined
      ? teams?.away.players["ID" + id] ?? teams?.home.players["ID" + id]
      : undefined;
  }

  getAwayPlayersParentTeamIds() {
    if (this.boxscore?.teams.away.players) {
      return _uniq(
        Object.keys(this.boxscore?.teams.away.players).map(
          (key) => this.boxscore?.teams.away.players[key].parentTeamId
        )
      );
    }
  }

  getHomePlayersParentTeamIds() {
    if (this.boxscore?.teams.home.players) {
      return _uniq(
        Object.keys(this.boxscore?.teams.home.players).map(
          (key) => this.boxscore?.teams.home.players[key].parentTeamId
        )
      );
    }
  }

  getVenueId(): number | undefined {
    return this.boxscore?.teams.home.team.venue.id;
  }

  // gumbo state
  isLiveAction(): boolean {
    return this.state.liveAction !== undefined ? this.state.liveAction : false;
  }

  inningOngoing(): boolean {
    return (
      this.state.inning !== undefined &&
      (this.state.inning.state == "top" || this.state.inning.state == "bottom")
    );
  }

  getInning(): { inningNumber: number; inningState: InningState } | undefined {
    if (this.state.inning) {
      return {
        inningNumber: this.state.inning.currentInning,
        inningState: this.state.inning.state,
      };
    }
  }

  playOngoing(): boolean {
    return this.state.play?.state === "start";
  }

  getPitchHand(): string {
    return this.state.hands.pitchHand == "L" ? "Left" : "Right";
  }

  getBatHand(): string {
    return this.state.hands.batHand == "L" ? "Left" : "Right";
  }

  getBattingTeamId(): number | undefined {
    return this.state.inning?.battingTeamId;
  }

  getFieldingTeamId(): number | undefined {
    return this.state.inning?.fieldingTeamId;
  }

  getPlayIndex(): number | undefined {
    return this.state.play?.index;
  }

  getStrikeZone(): { top?: number; bottom?: number } | undefined {
    return this.state.play?.strikezone;
  }

  getTrackPosition(trackUid: number): number | undefined {
    return this.state.trackPositions.get(trackUid);
  }

  getCount(): { balls?: number; strikes?: number; outs?: number } | undefined {
    return this.state.count;
  }

  //TODO: add venue in manifest later
  //used for dynamic lighting
  getVenueUTCOffset(): number | undefined {
    return 0;
  }

  printState() {
    console.log(this.state);
  }
}

export function timeInMsToGumboTimecode(epochMs: number): string | undefined {
  if (epochMs === 0) {
    // Clock hasn't been set yet? Just grab a timecode-less gumbo, which represents the current state of the game.
    return undefined;
  }
  const currentDateTime = new Date(epochMs);
  return `${currentDateTime.getUTCFullYear()}${(
    currentDateTime.getUTCMonth() + 1
  )
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCDate()
    .toString()
    .padStart(2, "0")}_${currentDateTime
    .getUTCHours()
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCMinutes()
    .toString()
    .padStart(2, "0")}${currentDateTime
    .getUTCSeconds()
    .toString()
    .padStart(2, "0")}`; //"YYYMMdd_HHmmss"
}
